import type {
  AbstractPartDefinition,
  Blueprint,
  ProductSettings,
} from "@project/emerge-types";
import { createContext, useContext } from "react";
import type { StoreApi, UseBoundStore } from "zustand";
import type {
  InternalProductApi,
  InternalProductState,
} from "./useProductViewer";

/**
 * Type for the product store hook that can be used with any product type
 */
export type ProductStoreHook<
  ProductType extends string = string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> = UseBoundStore<
  StoreApi<
    InternalProductState<
      ProductType,
      SettingsType,
      BlueprintType,
      PartDefinitionType
    > &
      InternalProductApi<
        ProductType,
        SettingsType,
        BlueprintType,
        PartDefinitionType
      >
  >
>;

/**
 * Base type for any product store - uses widest possible constraints
 * This allows the context to accept any specific product store type
 */
export type AnyProductStoreHook = ProductStoreHook<
  string,
  ProductSettings<string>,
  Blueprint,
  AbstractPartDefinition<string>
>;

/**
 * React Context for providing the product store instance.
 * This allows generic components in packages/emerge to use a type-safe
 * product store without depending on manufacturer-specific code.
 */
export const ProductStoreContext = createContext<AnyProductStoreHook | null>(
  null,
);

/**
 * Hook to access the product store from context.
 * Must be used within a ProductStoreProvider.
 *
 * The generic parameters allow you to specify the exact types if needed,
 * but in most cases the default base types are sufficient.
 *
 * @throws Error if used outside of ProductStoreProvider
 */
export function useProductStoreContext<
  ProductType extends string = string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
>(): ProductStoreHook<
  ProductType,
  SettingsType,
  BlueprintType,
  PartDefinitionType
> {
  const store = useContext(ProductStoreContext);

  if (!store) {
    throw new Error(
      "useProductStoreContext must be used within a ProductStoreProvider. " +
        "Make sure to wrap your component tree with <ProductStoreProvider store={yourStore}>.",
    );
  }

  // The cast is safe because:
  // 1. All product stores extend the same base structure
  // 2. TypeScript's structural typing ensures compatibility
  // 3. The provider enforces that a valid store is provided
  return store as unknown as ProductStoreHook<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  >;
}

/**
 * Provider component that makes a product store instance available
 * to all child components via React Context.
 *
 * Accepts any product store that extends the base ProductStoreHook type.
 * This is type-safe because all product stores share the same structure,
 * differing only in their generic type parameters.
 *
 * @example
 * ```tsx
 * import { useFlightCaseViewer } from '@project/manufacturer';
 *
 * function App() {
 *   return (
 *     <ProductStoreProvider store={useFlightCaseViewer}>
 *       <ProductViewerXR />
 *     </ProductStoreProvider>
 *   );
 * }
 * ```
 */
export const ProductStoreProvider = <
  ProductType extends string = string,
  SettingsType extends
    ProductSettings<ProductType> = ProductSettings<ProductType>,
  BlueprintType extends Blueprint = Blueprint,
  PartDefinitionType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
>({
  store,
  children,
}: {
  store: ProductStoreHook<
    ProductType,
    SettingsType,
    BlueprintType,
    PartDefinitionType
  >;
  children: React.ReactNode;
}) => {
  // Safe cast because all product stores are structurally compatible
  // with the base AnyProductStoreHook type
  const contextValue = store as unknown as AnyProductStoreHook;

  return (
    <ProductStoreContext.Provider value={contextValue}>
      {children}
    </ProductStoreContext.Provider>
  );
};
